import {Breakpoint, Device} from '../types/Breakpoint';
import {isResponsiveValue, ResponsiveValue} from '../types/ResponsiveValue';
import {useDevice} from './useDevice';

/**
 * Map device types to breakpoints for responsive value resolution
 */
const DEVICE_TO_BREAKPOINT: Record<Device, Breakpoint> = {
  mobile: 'base',
  tablet: 'sm',
  notebook: 'md',
  desktop: 'lg',
  printer: 'base',
};

/**
 * Breakpoint precedence order for fallback resolution (mobile-first)
 * If a value for the current breakpoint is not defined, fall back to the previous one
 */
const BREAKPOINT_ORDER: Breakpoint[] = ['base', 'sm', 'md', 'lg'];

/**
 * Hook to resolve a responsive value based on the current device
 * Returns the appropriate value for the current breakpoint with fallback logic
 * @param value - Responsive value object or simple value
 * @returns Resolved value for current device
 * @example const padding = useResponsiveValue({ base: 2, sm: 3, lg: 5 }); // Returns 2 on mobile, 3 on tablet, 5 on desktop
 * @example const padding = useResponsiveValue(4); // Returns 4 on all devices
 */
export const useResponsiveValue = <T>(value: T | ResponsiveValue<T>): T => {
  const device = useDevice();

  if (!isResponsiveValue(value)) {
    return value;
  }

  const currentBreakpoint = DEVICE_TO_BREAKPOINT[device];
  const currentBreakpointIndex = BREAKPOINT_ORDER.indexOf(currentBreakpoint);

  for (let i = currentBreakpointIndex; i >= 0; i--) {
    const breakpoint = BREAKPOINT_ORDER[i];
    const breakpointValue = value[breakpoint];
    if (breakpointValue !== undefined) {
      return breakpointValue;
    }
  }

  return value[BREAKPOINT_ORDER[0]] as T;
};
